import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'

import tr from './locales/tr.json'

/**
 * Cok dilli arayuz.
 *
 * ## Neden `I18nextProvider` YOK
 *
 * `initReactI18next` VARSAYILAN i18next ornegine baglaniyor ve
 * `useTranslation()` onu global olarak buluyor. Bir provider eklemek, bu
 * kod tabanindaki ~30 test dosyasini kirardi: onlar bilesenleri
 * `SessionProvider`i mock'layarak, YALIN halde render ediyor. Ceviri bir
 * saglayici zinciri gerektirseydi her test dosyasina bir sarmalayici
 * eklemek gerekirdi - ve unutulan her biri "anahtar yerine ham metin"
 * seklinde SESSIZCE gecen bir test uretirdi.
 *
 * Ayni sebeple `init()` bu modulun GOVDESINDE cagriliyor (import yan
 * etkisi). Bilincli: bir bilesen `t`yi kullaniyorsa bu modulu zaten import
 * etmis oluyor, dolayisiyla ilk render'da katalog HER ZAMAN hazir.
 *
 * ## Varsayilan dil SENKRON paketleniyor, digerleri LAZY
 *
 * `tr` bundle'in icinde (~6 KB). Sebebi: varsayilan dil ilk render'da
 * hazir olmak ZORUNDA. Onu da lazy yapmak ya `<Suspense>` ya da ham
 * anahtarlarin bir an ekranda gorunmesi demekti - 6 KB'lik kazanc icin
 * kotu bir takas. Diger diller `import()` ile, yalnizca secildiginde
 * iniyor.
 *
 * ## Yeni dil eklemek
 *
 * 1. `locales/<kod>.json` olustur (`keywords.json`daki TUM anahtarlar).
 * 2. `LOCALES`e bir satir ekle (`label` o dilin KENDI adiyla yazilir -
 *    "Deutsch", "Almanca" degil; dil secicisini kullanan kisi zaten o
 *    dili ariyor).
 * 3. `keywords.json` -> `meta.active_locales`a kodu ekle.
 *
 * Baska hicbir yere dokunmak gerekmiyor. `catalog.test.ts` eksik veya
 * fazla anahtari, uyusmayan yer tutucuyu ve o dilin GEREKTIRDIGI cogul
 * kategorilerini derleme oncesi yakaliyor.
 */

/** Ceviri kaynagi: tum anahtarlarin dogru oldugu kabul edilen dil. */
export const SOURCE_LOCALE = 'tr'

export interface LocaleInfo {
  /** Dilin KENDI adindaki etiketi. */
  readonly label: string
  /** Yazim yonu - Arapca/Ibranice eklendiginde `rtl` olur. */
  readonly dir: 'ltr' | 'rtl'
  /** `Intl` ve `<html lang>` icin BCP-47 etiketi. */
  readonly tag: string
}

export const LOCALES: Record<string, LocaleInfo> = {
  tr: { label: 'Türkçe', dir: 'ltr', tag: 'tr-TR' },
  en: { label: 'English', dir: 'ltr', tag: 'en-US' },
  it: { label: 'Italiano', dir: 'ltr', tag: 'it-IT' },
  // `-u-nu-latn`: Intl tarih/sayi bicimleri Bati rakamlariyla (0-9). Ceviri
  // metinleri de Bati rakami kullaniyor; ikisi karisik gorunmesin.
  ar: { label: 'العربية', dir: 'rtl', tag: 'ar-u-nu-latn' },
}

export type LocaleCode = keyof typeof LOCALES & string

export const LOCALE_CODES = Object.keys(LOCALES)

/** Tarayici anahtari - sunucu ayari okunamadiginda kullanilir. */
const STORAGE_KEY = 'hedefai.locale'

/**
 * Bir dil kodunu DESTEKLENEN bir koda indirger, yoksa `null` doner.
 *
 * `en-GB` -> `en` daraltmasi sart: `navigator.language` neredeyse her zaman
 * bolgeli geliyor ve tam eslesme arayan bir kontrol Ingilizce tarayicilari
 * sessizce Turkce'ye dusururdu.
 */
export function normalizeLocale(value: string | null | undefined): LocaleCode | null {
  if (!value) return null
  const lowered = value.toLowerCase()
  if (lowered in LOCALES) return lowered
  const base = lowered.split('-')[0]
  return base in LOCALES ? base : null
}

/**
 * Baslangic dili. Sira: tarayici hafizasi -> tarayici dili -> kaynak dil.
 *
 * Sunucudaki `user_settings.locale` bu listede YOK cunku oturum henuz
 * yuklenmedi. Oturum gelince `applyServerLocale()` devrali - ve o, cihaz
 * tercihini EZER, cunku kullanicinin acikca kaydettigi ayar tarayici
 * tahmininden daha guclu bir sinyal.
 */
function initialLocale(): LocaleCode {
  if (typeof window === 'undefined') return SOURCE_LOCALE
  const stored = normalizeLocale(safeRead(STORAGE_KEY))
  if (stored) return stored
  return normalizeLocale(navigator.language) ?? SOURCE_LOCALE
}

function safeRead(key: string): string | null {
  // Gizli sekmede / depolama kapaliyken `localStorage` erisimi ATIYOR.
  // Dil tercihi ugruna uygulamayi acilmaz yapmak kabul edilemez.
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeWrite(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    /* yok sayilir - yukaridaki gerekce */
  }
}

void i18next.use(initReactI18next).init({
  lng: initialLocale(),
  fallbackLng: SOURCE_LOCALE,
  resources: { tr: { translation: tr } },
  // DUZ anahtarlar: `auth.login.title` bir YOL degil, harfi harfine
  // anahtarin kendisi. Boylece `keywords.json` kayit defteriyle birebir
  // ayni sekle sahip oluyor ve ic ice sozluk belirsizligi hic dogmuyor.
  keySeparator: false,
  nsSeparator: false,
  // React zaten kacisi yapiyor; ikinci kez kacirmak Turkce kesme ve
  // tirnak isaretlerini `&#39;`e cevirip EKRANDA gosterirdi.
  interpolation: { escapeValue: false },
  returnNull: false,
})

applyDocumentLocale(i18next.language)

/** Yuklenmis diller - ayni dili iki kez indirmemek icin. */
const loaded = new Set<string>([SOURCE_LOCALE])

/**
 * Baslangic dili kaynak dil DEGILSE katalogunu indir.
 *
 * ## Bu bir HATA duzeltmesiydi ve yalnizca E2E yakaladi
 *
 * `init({ lng: 'en', resources: { tr } })` sessizce yanlis calisiyordu:
 * `i18next.language` 'en' oluyor, `<html lang>` 'en' yaziliyor, ama `en`
 * katalogu YUKLU OLMADIGI icin her anahtar `fallbackLng`e (tr) dusuyor.
 * Yani kullanici Ingilizceyi secip sayfayi yeniliyor ve arayuz TURKCE
 * geri geliyor - ustelik `lang="en"` oldugu icin "dil degismedi" gibi de
 * gorunmuyor, sadece metinler cevrilmemis oluyor.
 *
 * Birim testleri bunu goremez: katalog dosyalari tutarli, `t()` de dogru
 * calisiyor. Eksik olan sey KATALOGUN INDIRILMESI ve o yalnizca gercek bir
 * sayfa yuklemesinde ortaya cikiyor.
 */
if (i18next.language !== SOURCE_LOCALE) {
  void setLocale(i18next.language)
}

/**
 * Dili degistirir; katalog eksikse once indirir.
 *
 * Indirme basarisiz olursa dil DEGISMIYOR ve `false` donuyor. Yarim
 * cevrilmis bir ekran gostermek yerine mevcut dilde kalmak dogru: ag
 * hatasi bir dil tercihini kalici olarak bozmamali.
 */
export async function setLocale(next: string): Promise<boolean> {
  const code = normalizeLocale(next)
  if (!code) return false
  if (code === i18next.language && loaded.has(code)) return true

  if (!loaded.has(code)) {
    const bundle = await loadCatalog(code)
    if (!bundle) return false
    i18next.addResourceBundle(code, 'translation', bundle, true, true)
    loaded.add(code)
  }

  await i18next.changeLanguage(code)
  safeWrite(STORAGE_KEY, code)
  applyDocumentLocale(code)
  return true
}

/**
 * Sunucudan gelen tercihi uygular.
 *
 * `SessionProvider` oturumu okuyunca cagriliyor. Cihazda kayitli degerden
 * FARKLIYSA sunucu kazaniyor: kullanici bunu ayarlar ekraninda acikca
 * kaydetti, bu tarayici tahmininden guclu bir sinyal.
 */
export function applyServerLocale(value: string | null | undefined): void {
  const code = normalizeLocale(value)
  // `loaded` kontrolu SART: dil zaten "etkin" gorunuyor olabilir ama
  // katalogu inmemis olabilir (bkz. yukaridaki baslangic duzeltmesi).
  // Yalnizca `code === i18next.language`e bakmak, o durumda sessizce
  // cevrilmemis bir arayuz birakirdi.
  if (!code || (code === i18next.language && loaded.has(code))) return
  void setLocale(code)
}

async function loadCatalog(code: string): Promise<Record<string, string> | null> {
  try {
    // Acik `switch`: dinamik `import(`./locales/${code}.json`)` Vite'in
    // hangi dosyalari bundle'a katacagini bilmesini engelliyor ve uretimde
    // 404 uretiyor. Yeni dil eklerken buraya bir satir gerekiyor.
    switch (code) {
      case 'en':
        return (await import('./locales/en.json')).default
      case 'it':
        return (await import('./locales/it.json')).default
      case 'ar':
        return (await import('./locales/ar.json')).default
      default:
        return null
    }
  } catch {
    return null
  }
}

function applyDocumentLocale(code: string): void {
  if (typeof document === 'undefined') return
  const info = LOCALES[code] ?? LOCALES[SOURCE_LOCALE]
  document.documentElement.lang = code
  document.documentElement.dir = info.dir
}

/** Etkin dil - `Intl` bicimlendiricileri icin BCP-47 etiketi. */
export function localeTag(): string {
  return (LOCALES[i18next.language] ?? LOCALES[SOURCE_LOCALE]).tag
}

export { i18next }
